// The ONE soft "you're approaching a limit" toast (mkt-0920-dev-soft-cap-warnings).
//
// Sibling of utils/storageLimitToast.jsx, which shows the BLOCKING "you are
// out of storage" toast. This one is deliberately the opposite in every way
// that matters: it never blocks the action that triggered it, it uses the
// neutral/info tone (not error), and it shows at most ONCE PER SESSION per
// limit `key` — a user who is at 4 of 5 Wheels for an entire session should
// see this once, not on every Gallery render or every clone attempt.
//
// Same shared-toast reasoning as storageLimitToast.jsx: every surface that
// nears a cap calls THIS, so the wording, the Upgrade affordance and the
// dismiss behaviour are identical everywhere a soft warning appears (storage
// upload flow, Create Wheel entry + Gallery, the clone action) — never three
// surfaces quietly telling the same user three different stories.
import React from "react";
import { toast } from "react-toastify";

const SESSION_KEY_PREFIX = "near-limit-shown:";

/**
 * Has this session already shown the near-limit toast for `key`
 * ("storage" | "created-wheels" | "cloned-wheels")?
 *
 * Reads sessionStorage defensively: a private-browsing / storage-disabled
 * context must never throw here, it should just re-show the toast (the
 * least surprising failure — an extra toast, not a silent feature loss).
 */
export const hasShownNearLimitToast = (key) => {
  try {
    return window.sessionStorage.getItem(SESSION_KEY_PREFIX + key) === "1";
  } catch (err) {
    return false;
  }
};

export const markNearLimitToastShown = (key) => {
  try {
    window.sessionStorage.setItem(SESSION_KEY_PREFIX + key, "1");
  } catch (err) {
    // storage unavailable — the toast just shows again next attempt, harmless.
  }
};

/** Test-only: forget every "already shown" flag so a suite can assert the
 * once-per-session behaviour without depending on run order. */
export const resetNearLimitToastsForTests = () => {
  try {
    Object.keys(window.sessionStorage)
      .filter((k) => k.startsWith(SESSION_KEY_PREFIX))
      .forEach((k) => window.sessionStorage.removeItem(k));
  } catch (err) {
    // no-op
  }
};

export const NearLimitToastBody = ({ message, onUpgrade }) => (
  <span>
    {message}{" "}
    <a
      href="/subscription"
      data-testid="near-limit-upgrade-link"
      onClick={(event) => {
        if (onUpgrade) {
          event.preventDefault();
          onUpgrade();
        }
      }}
    >
      Upgrade
    </a>
  </span>
);

/**
 * Show the shared "approaching your limit" toast for `key`, at most once per
 * browser session for that key. NEVER blocks the caller — call this and keep
 * going; it is purely informational, dismissible (react-toastify's default
 * close button), and auto-closes like any other info toast.
 *
 * @param {string} key which limit this is for — "storage" | "created-wheels"
 *   | "cloned-wheels" — also the sessionStorage flag and the toast id, so a
 *   rapid double-fire (e.g. two cards mounting at once) collapses to one toast.
 * @param {string} message the sentence to show, e.g. "4 of 5 Wheels used."
 * @param {function} [onUpgrade] optional in-app upgrade handler.
 */
export const showNearLimitToast = (key, message, onUpgrade) => {
  if (hasShownNearLimitToast(key)) return;
  markNearLimitToastShown(key);
  toast.info(<NearLimitToastBody message={message} onUpgrade={onUpgrade} />, {
    toastId: `near-limit-${key}`,
  });
};
